import { useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import { EViewSolarEstimationSolarProductCategory } from "../../../../shared/oversight-core/enums/view-solar-estimation-solar-product-category";
import MaterialIcon from "../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import { formatWithCommas } from "../../../../shared/oversight-core/utils/format-with-commas";
import Helper from "../../../../shared/oversight-core/utils/helpers";
import styles from "./solar-component-option.module.scss";

interface IProps {
  id: string;
  isRecommended?: boolean;
  category: EViewSolarEstimationSolarProductCategory;
  name: string;
  maxOperatingVoltage: number;
  unit: string;
  price: number;
  selected: string;
  onSelect: (
    category: EViewSolarEstimationSolarProductCategory,
    id: string
  ) => void;
  liters: number;
  ratedPower: number;
  amperage: number;
  batteryVoltage: number;
}

const SolarComponentOption = (props: IProps) => {
  const {
    id,
    isRecommended = false,
    category,
    name,
    maxOperatingVoltage,
    unit,
    price,
    selected,
    onSelect,
    liters,
    batteryVoltage,
    amperage,
    ratedPower,
  } = props;

  const getDeviceContent = useMemo(() => {
    switch (category) {
      case EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS:
        return <></>;
      case EViewSolarEstimationSolarProductCategory.WATER_HEATERS:
        return (
          <>
            <Row>
              <Col className="text-advance-description font-size-12">
                Tank Size : {liters}L
              </Col>
            </Row>
          </>
        );
      case EViewSolarEstimationSolarProductCategory.PANELS:
        return (
          <>
            <Row>
              <Col className="text-advance-description font-size-12">
                Rated Power : {ratedPower}W
              </Col>
            </Row>
          </>
        );
      case EViewSolarEstimationSolarProductCategory.BATTERIES:
        return (
          <>
            <Row>
              <Col className="text-advance-description font-size-12">
                Max Operating Voltage : {maxOperatingVoltage}V
              </Col>
            </Row>
            <Row>
              <Col className="text-advance-description font-size-12">
                Amperage : {amperage}A
              </Col>
            </Row>
          </>
        );
      case EViewSolarEstimationSolarProductCategory.INVERTERS:
        return (
          <>
            <Row>
              <Col className="text-advance-description font-size-12">
                Rated Power : {ratedPower}W
              </Col>
            </Row>
            <Row>
              <Col className="text-advance-description font-size-12">
                Operating Voltage : {batteryVoltage}V
              </Col>
            </Row>
          </>
        );
    }
  }, [
    category,
    liters,
    ratedPower,
    maxOperatingVoltage,
    amperage,
    batteryVoltage,
  ]);

  return (
    <div
      className={`px-4 py-3 rounded-3 cursor-pointer h-100 ${
        selected === id ? styles.selectedComponent : ""
      } ${
        isRecommended
          ? `${styles.solarComponentOptionRecommended} bg-background-7`
          : `${styles.solarComponentOptionNotRecommended} bg-background-8`
      }`}
      onClick={() => {
        onSelect(category, id);
      }}
      style={{ width: "300px" }}
    >
      <Row className="align-items-center justify-content-between">
        <Col className="col-auto">
          <MaterialIcon
            icon={
              category === EViewSolarEstimationSolarProductCategory.INVERTERS
                ? "tablet_mac"
                : category ===
                  EViewSolarEstimationSolarProductCategory.BATTERIES
                ? "battery_horiz_000"
                : category === EViewSolarEstimationSolarProductCategory.PANELS
                ? "solar_power"
                : category ===
                  EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS
                ? "google_tv_remote"
                : category ===
                  EViewSolarEstimationSolarProductCategory.WATER_HEATERS
                ? "propane"
                : "tune"
            }
            color="var(--color-component-icon)"
          />
        </Col>
        {isRecommended && (
          <Col className="text-color-3 font-size-12 font-size-500 col-auto">
            Recommended
          </Col>
        )}
      </Row>
      <Row className="mt-2">
        <Col className="title-4 font-weight-500">{name}</Col>
      </Row>
      {getDeviceContent}
      <Row className="mt-2">
        <Col className="text-color-5 font-weight-500 font-size-12">
          {unit}&nbsp;{formatWithCommas(Number(Helper.roundTo2(price)))}
        </Col>
      </Row>
    </div>
  );
};

export default SolarComponentOption;
